import React, { useState, useEffect } from 'react';
import { Download, Zap, Database, CheckCircle2, ArrowRight, PlayCircle, Globe } from 'lucide-react';
import DocumentViewerModal from '../components/DocumentViewerModal';
import { fetchDocumentsConfig, DEFAULT_DOCUMENTS_CONFIG } from '../../../utils/documentsConfig';

function AutoPlayVideo({ filenames, className }) {
  const videoRef = React.useRef(null);

  React.useEffect(() => {
    const vid = videoRef.current;
    if (vid) {
      // Set volume to 1.0 so when user unmutes, sound plays at full volume
      vid.volume = 1.0;

      const tryPlay = () => {
        const p = vid.play();
        if (p !== undefined) {
          p.catch((err) => {
            console.warn("Autoplay deferred by browser policy:", err);
          });
        }
      };

      tryPlay();
      vid.addEventListener('canplaythrough', tryPlay, { once: true });
    }
  }, [filenames]);

  const publicUrl = process.env.PUBLIC_URL || '';

  return (
    <video
      ref={videoRef}
      autoPlay
      loop
      muted
      playsInline
      controls
      preload="auto"
      className={className}
    >
      {filenames.map((name, i) => {
        const isMp4 = name.toLowerCase().endsWith('.mp4');
        const mimeType = isMp4 ? 'video/mp4' : 'video/webm';
        return (
          <React.Fragment key={i}>
            <source src={`${publicUrl}/assets/${name}`} type={mimeType} />
            <source src={`/assets/${name}`} type={mimeType} />
          </React.Fragment>
        );
      })}
      Your browser does not support HTML5 video playback.
    </video>
  );
}

export default function MigrationsPage({ onOpenDownload, onOpenLogin, onOpenContact, onOpenDemo }) {
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [docConfig, setDocConfig] = useState(DEFAULT_DOCUMENTS_CONFIG);

  useEffect(() => {
    fetchDocumentsConfig().then(cfg => setDocConfig(cfg));
  }, []);

  const openDocumentModal = (docId, e) => {
    if (e) e.preventDefault();
    setSelectedDocumentId(docId);
    setIsDocModalOpen(true);
  };
  return (
    <div className="animate-fade-in bg-white min-h-screen text-[#0f172a]">
      {/* HERO SECTION */}
      <section className="relative pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-[1200px] mx-auto text-center">
        {/* Subtle background element */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-[1000px] bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-50/50 via-white to-white -z-10" />

        <div className="inline-block px-3 py-1 mb-6 rounded-full bg-blue-50 text-blue-600 text-xs font-bold tracking-widest uppercase border border-blue-100 shadow-sm">
          Enterprise Dialect Migrations · Dialect Transformation Blueprint
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mb-6 max-w-4xl mx-auto">
          Convert 100+ Legacy SQL Server Scripts to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Oracle</span> Without Inventing <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">Schema Objects</span>.
        </h1>
        <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto font-medium">
          Stop manually rewriting T-SQL identities, datetime functions, temporary tables, and proprietary SQL Server syntax for Oracle. Candor reads your source SQL Server and target Oracle schemas simultaneously using live system catalogs or imported DDL to map and rewrite dialect constructs with structural context, instead of guessing columns from text alone.
        </p>
        <p className="text-base text-slate-500 mb-10 max-w-3xl mx-auto">
          Whether you are modernizing warehouse SQL, consolidating analytics onto an Oracle estate, or moving application logic toward Oracle SQL/PL·SQL-ready patterns, Candor helps teams reduce manual refactoring by combining AI-assisted transformation with schema-aware validation.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={onOpenDownload}
            className="px-5 py-3 bg-[#0D7A54] hover:bg-[#0A6343] text-white font-bold rounded-lg shadow-sm transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
          >
            <Zap className="w-5 h-5 fill-current" /> Try Sandbox
          </button>
          <button
            onClick={onOpenDownload}
            className="px-5 py-3 bg-[#2563EB] hover:bg-blue-700 text-white font-bold rounded-lg shadow-sm transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
          >
            <Download className="w-5 h-5" /> Download IDE
          </button>
          <button
            onClick={onOpenLogin}
            className="px-5 py-3 bg-white text-[#1E3A8A] font-bold rounded-lg border border-gray-300 hover:border-[#2563EB] hover:text-[#2563EB] shadow-sm transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
          >
            <Globe className="w-5 h-5" /> Open Web IDE
          </button>
          <button
            onClick={onOpenDemo || onOpenContact}
            className="px-5 py-3 bg-[#4F39C2] hover:bg-[#402da1] text-white font-bold rounded-lg shadow-sm transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
          >
            <PlayCircle className="w-5 h-5" /> Watch Demo
          </button>
        </div>
      </section>

      {/* WHY SQL SERVER → ORACLE */}
      <section className="bg-gradient-to-br from-slate-900 via-[#0B1120] to-slate-800 text-white py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Subtle accent glow */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />

        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-12 items-center relative z-10">
          <div>
            <h2 className="text-3xl lg:text-4xl font-extrabold mb-6">Why teams migrate SQL Server workloads toward Oracle</h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Many enterprises standardize on Oracle for existing operational systems, licensed estates, compliance boundaries, or long-running PL/SQL applications. Candor is for teams who must move or dual-run SQL Server logic onto Oracle without months of hand translation.
            </p>
            <p className="text-sm text-slate-400/80 italic border-l-2 border-slate-700 pl-4">
              Avoid overselling Oracle as “generic cloud-native warehouse.” Speak to enterprise Oracle estates, DBA workflows, and dual-schema discipline.
            </p>
          </div>
          <div className="space-y-4">
            {[
              "Align analytics and application SQL with an established Oracle database of record",
              "Reduce custom T-SQL surface area that only specialists can maintain",
              "Consolidate dialects when Oracle is the mandated platform standard",
              "Support modernization programs that require Oracle SQL/PL·SQL compatibility paths",
              "Cut repetitive dialect rewrites while keeping engineers accountable for review"
            ].map((item, i) => (
              <div key={i} className="group flex gap-4 p-5 bg-slate-800/40 hover:bg-slate-800/80 rounded-xl border border-slate-700/50 hover:border-slate-600 transition-all duration-300">
                <CheckCircle2 className="w-6 h-6 text-blue-400 shrink-0 group-hover:text-blue-300 transition-colors" />
                <p className="text-slate-200 group-hover:text-white transition-colors">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW CONVERSION WORKS & OBJECTS SUPPORTED */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 max-w-[1200px] mx-auto">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-6">Schema-aware SQL conversion</h2>
            <p className="text-lg text-slate-600 mb-8">
              Candor assists with transforming T-SQL into Oracle-compatible SQL (and procedure-oriented PL/SQL patterns where applicable) by using metadata and dialect rules, not prompt-only guessing:
            </p>
            <ul className="space-y-4">
              {[
                "TOP / row-limit constructs → FETCH FIRST (or project-approved ROWNUM strategy)",
                "SQL Server functions → Oracle equivalents (datetime, null, string, etc.)",
                "Data type mapping guided by source + target type metadata",
                "Identifier and naming compatibility",
                "Syntax validation and unresolved-object highlights for human review",
                "Multi-file scripts in one workspace for batch migration programs"
              ].map((item, i) => (
                <li key={i} className="flex gap-3 text-slate-700">
                  <ArrowRight className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-6">Objects Candor can assist with</h2>
            <p className="text-lg text-slate-600 mb-8">
              Candor assists with migration workflows involving a wide range of SQL Server assets. Complex procedural objects often require engineer review after conversion.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                "SQL queries and scripts",
                "Views",
                "Stored procedures (toward Oracle SQL / PL·SQL patterns, review required)",
                "Common Table Expressions (CTEs)",
                "Joins and nested queries",
                "Window functions",
                "User-defined functions (UDFs) where mapping is supported",
                "Schemas, tables, and constraint definitions (via DDL / catalog context)",
                "Temporary-table patterns and data-type conversions"
              ].map((item, i) => (
                <div key={i} className="p-4 bg-slate-50 rounded-lg border border-slate-100 flex gap-3 text-sm font-medium text-slate-700">
                  <Database className="w-4 h-4 text-slate-400 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TYPICAL MIGRATION WORKFLOW */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-100">
        <div className="max-w-[1000px] mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl font-extrabold text-slate-900 mb-4 tracking-tight">Recommended workflow</h2>
            <p className="text-lg text-slate-600">A structured path from schema connection to Oracle deployment.</p>
          </div>

          <div className="space-y-8">
            {/* Step 1: Connect (Title, Description, and Video Underneath - No Side Borders) */}
            <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 font-bold rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-lg shadow-sm shrink-0">
                    1
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Connect</h3>
                </div>
                <a
                  href={`${process.env.PUBLIC_URL || ''}${docConfig.oracleConnectionGuide?.url || '/documents/Oracle%20Database%20_%20UserGuide.pdf'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => openDocumentModal('oracle-connection-guide', e)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800 bg-blue-50/80 hover:bg-blue-100 px-3.5 py-1.5 rounded-lg border border-blue-200/70 transition-all shadow-xs cursor-pointer"
                >
                  {docConfig.oracleConnectionGuide?.title || 'Candor Oracle Database Connection Guide'}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <p className="text-slate-600 leading-relaxed text-base mb-5 pl-1">
                Connect SQL Server and Oracle (or import DDL for source and/or target).
              </p>
              <div className="rounded-xl overflow-hidden shadow-sm border-0 bg-transparent">
                <AutoPlayVideo
                  filenames={["oracle-database.mp4"]}
                  className="w-full h-auto rounded-xl block border-0 outline-none shadow-sm"
                />
              </div>
            </div>

            {/* Step 2: Analyze */}
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex gap-4 items-start shadow-sm">
              <div className="w-9 h-9 font-bold rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-base shrink-0 mt-0.5 shadow-sm">
                2
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Analyze</h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Candor analyzes objects, dependencies, and dialect constructs against both schemas.
                </p>
              </div>
            </div>

            {/* Step 3: Convert (Title, Description, and Video Underneath - No Side Borders) */}
            <div className="p-6 md:p-8 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 font-bold rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-lg shadow-sm shrink-0">
                    3
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Convert</h3>
                </div>
                <a
                  href={`${process.env.PUBLIC_URL || ''}${docConfig.etlScriptGuide?.url || '/documents/ETL_userguide.pdf'}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => openDocumentModal('etl-script-guide', e)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-800 bg-violet-50/80 hover:bg-violet-100 px-3.5 py-1.5 rounded-lg border border-violet-200/70 transition-all shadow-xs cursor-pointer"
                >
                  {docConfig.etlScriptGuide?.title || 'Candor ETL Script Generation and Execution Guide'}
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <p className="text-slate-600 leading-relaxed text-base mb-5 pl-1">
                Assisted conversion produces Oracle-oriented SQL (and PL/SQL-pattern drafts).
              </p>
              <div className="rounded-xl overflow-hidden shadow-sm border-0 bg-transparent">
                <AutoPlayVideo
                  filenames={["etl-with-audio-3.mp4"]}
                  className="w-full h-auto rounded-xl block border-0 outline-none shadow-sm"
                />
              </div>
            </div>

            {/* Step 4: Review */}
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex gap-4 items-start shadow-sm">
              <div className="w-9 h-9 font-bold rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-base shrink-0 mt-0.5 shadow-sm">
                4
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Review</h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Engineer reviews diffs, resolves flagged objects, and validates in a non-prod target.
                </p>
              </div>
            </div>

            {/* Step 5: Deploy */}
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-2xl flex gap-4 items-start shadow-sm">
              <div className="w-9 h-9 font-bold rounded-full bg-rose-100 text-rose-700 flex items-center justify-center text-base shrink-0 mt-0.5 shadow-sm">
                5
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Deploy</h3>
                <p className="text-slate-600 text-base leading-relaxed">
                  Export and deploy to Oracle with reduced pure hand-rewrite effort.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IDEAL USE CASES */}
      <section className="bg-slate-900 text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="text-3xl font-extrabold mb-10 text-center">Ideal Use Cases</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              "Migrating SQL Server warehouse or reporting SQL toward Oracle estates",
              "Modernizing legacy application SQL that must run on Oracle",
              "Consolidating multi-dialect analytics where Oracle is the standard",
              "Reducing multi-week hand translation of large T-SQL script portfolios",
              "Establishing a dual-schema review process for controlled cloud/OCI/on-prem Oracle programs"
            ].map((useCase, i) => (
              <div key={i} className="flex gap-4 items-start bg-slate-800/50 p-6 rounded-2xl border border-slate-700/50 hover:bg-slate-800 transition-colors">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
                <p className="text-slate-300 font-medium leading-relaxed">{useCase}</p>
              </div>
            ))}
          </div>
        </div>
      </section>




      {/* FAQ REMOVED */}

      {/* CLOSING CTA */}
      <section className="bg-blue-600 text-white py-24 px-4 sm:px-6 lg:px-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-white via-transparent to-transparent" />
        <div className="max-w-[800px] mx-auto relative z-10">
          <h2 className="text-4xl font-extrabold mb-6">Start your SQL Server to Oracle migration with schema awareness</h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Connect catalogs or import DDL. Convert multi-construct T-SQL with dual-schema context. Review Oracle SQL/PL·SQL outputs before you promote.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12">
            <button
              onClick={onOpenDownload}
              className="px-5 py-3 bg-[#0D7A54] hover:bg-[#0A6343] text-white font-bold rounded-lg shadow-sm transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <Zap className="w-5 h-5 fill-current" /> Try Sandbox
            </button>
            <button
              onClick={onOpenDownload}
              className="px-5 py-3 bg-white text-blue-600 hover:bg-slate-50 font-bold rounded-lg shadow-lg shadow-black/10 transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <Download className="w-5 h-5" /> Download IDE
            </button>
            <button
              onClick={onOpenLogin}
              className="px-5 py-3 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-lg shadow-sm transition-colors border border-blue-500 text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <Globe className="w-5 h-5" /> Open Web IDE
            </button>
            <button
              onClick={onOpenDemo || onOpenContact}
              className="px-5 py-3 bg-[#4F39C2] hover:bg-[#402da1] text-white font-bold rounded-lg shadow-sm transition-colors text-[15px] whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <PlayCircle className="w-5 h-5" /> Watch Demo
            </button>
          </div>
          <p className="text-blue-200 font-medium tracking-wide text-sm">
            Candor · Schema-aware T-SQL to Oracle migration workspace for data engineering teams.
          </p>
        </div>
      </section>

      {/* Interactive Document Viewer Modal */}
      <DocumentViewerModal
        isOpen={isDocModalOpen}
        onClose={() => setIsDocModalOpen(false)}
        documentId={selectedDocumentId}
      />
    </div>
  );
}
